import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { usePermissions } from '@/features/permissions/hooks/use-permissions';
import { usePatient } from '@/features/emr/api/patients-api';
import { usePregnancies } from '@/features/emr/api/pregnancies-api';
import { useLabAction, useLabExtraction, type LabValue } from './api';

export function labFailureExplanation(code?: string) {
  switch (code) {
    case 'page_too_large':
      return 'The saved page exceeds the extraction size limit. The processing service must split it into smaller sections.';
    case 'page_evidence_unverified':
      return 'At least one proposed test name, result, or supporting quote did not match the saved OCR text. The entire page was withheld. This run did not retain the rejected row, so it cannot identify a specific clinical value for you to confirm.';
    case 'page_output_unverified':
      return 'The model output could not be verified. This older run did not retain a more specific cause or the rejected row; it is not evidence of an error in the original record.';
    case 'page_response_incomplete':
      return 'The model response ended before the page analysis was complete. Processing must be retried; no clinical clarification has been identified.';
    case 'page_schema_invalid':
      return 'The model response did not match the required laboratory data format. This is a processing problem, not a clinical finding to approve.';
    case 'page_model_unavailable':
      return 'The model service did not finish the request. Retry processing; no clinical clarification has been identified.';
    default:
      return 'Processing did not finish and no specific cause was retained. Retry once; if it recurs, the processing service needs investigation.';
  }
}

export function LabReview({
  documentId,
  patientId,
  primaryStatus,
  revisionId,
}: {
  documentId: string;
  patientId?: string | null;
  primaryStatus?: string | null;
  revisionId?: string | null;
}) {
  const { can } = usePermissions();
  const read = can('read_document');
  const query = useLabExtraction(documentId, read, primaryStatus, revisionId);
  const extract = useLabAction(documentId, 'extract');
  const primaryBusy = ['pending', 'extracting'].includes(primaryStatus ?? '');
  const primaryFailed = primaryStatus === 'failed';
  if (!read) return null;
  return (
    <section className="flex flex-col gap-3 rounded-md border p-4" aria-label="Laboratory extraction">
      <h3 className="font-medium">Laboratory results</h3>
      <p className="text-sm text-muted-foreground">
        Lab values are analyzed with document extraction. Review and select results below before adding them to the
        Maternal Flowsheet.
      </p>
      {query.isError && (
        <p role="alert">
          Unable to load laboratory extraction.{' '}
          <Button variant="link" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </p>
      )}
      {query.isLoading && <p>Loading laboratory extraction…</p>}
      {query.data && (
        <>
          <p className="text-sm text-muted-foreground">
            {query.data.status === 'not_started'
              ? primaryBusy
                ? 'Laboratory analysis will start automatically after document text extraction.'
                : primaryFailed
                  ? 'Retry document text extraction above to start laboratory analysis.'
                  : 'Laboratory analysis has not run for this document yet.'
              : `Extraction: ${query.data.status}${query.data.total_pages ? ` · ${query.data.completed_pages}/${query.data.total_pages} pages` : ''}`}
          </p>
          {['not_started', 'failed'].includes(query.data.status) &&
            !primaryBusy &&
            !primaryFailed &&
            can('update_document') && (
              <Button
                variant="outline"
                disabled={extract.isPending}
                onClick={() => extract.mutate({}, { onError: () => toast.error('Unable to queue extraction') })}>
                {query.data.status === 'failed' ? 'Retry lab extraction' : 'Analyze laboratory results'}
              </Button>
            )}
          {query.data.status === 'failed' && (
            <div role="alert" className="flex flex-col gap-2">
              <p>
                Laboratory processing is incomplete; this does not mean the medical record is incorrect. No results from
                this run were added to the chart. Retry lab extraction reuses saved document text and retains completed
                pages when the source is unchanged. Once processing finishes, select results, choose the pregnancy,
                confirm source review, and save reviewed results below.
              </p>
              {query.data.pages.some((p) => p.processing_status === 'failed') && (
                <details open>
                  <summary className="cursor-pointer">
                    Pages with processing failures (
                    {query.data.pages.filter((p) => p.processing_status === 'failed').length})
                  </summary>
                  <p className="text-sm text-muted-foreground">
                    These are extraction failures, not requests to approve an unspecified clinical finding. If the
                    original page is readable, use Retry lab extraction. If the same failure returns, the extraction
                    needs correction; repeatedly confirming the document cannot resolve it.
                  </p>
                  {query.data.pages
                    .filter((p) => p.processing_status === 'failed')
                    .map((p) => (
                      <section
                        key={p.page}
                        className="flex flex-col gap-2 py-3"
                        aria-label={`Processing failure on page ${p.page}`}>
                        <p>
                          <strong>Page {p.page}:</strong> {labFailureExplanation(p.processing_error)}
                        </p>
                        <details>
                          <summary className="cursor-pointer">Inspect saved OCR text for page {p.page}</summary>
                          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-sm">
                            {p.lines?.join('\n') || 'No saved page text is available. Review the original document.'}
                          </pre>
                        </details>
                        <p className="text-sm text-muted-foreground">
                          Diagnostic: {p.processing_error || 'page_extraction_failed'}
                        </p>
                      </section>
                    ))}
                </details>
              )}
              {query.data.results.length > 0 && (
                <details>
                  <summary className="cursor-pointer">
                    View values found so far ({query.data.results.length}; not ready to save)
                  </summary>
                  <ul className="flex flex-col gap-2 pt-2">
                    {query.data.results.map((row, index) => (
                      <li key={index}>
                        {row.test_name}: {row.value} {row.unit} · Page {row.page}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {!query.data.pages.some((p) => p.processing_status === 'failed') && (
                <p>
                  The earlier run did not record a page-specific cause. If retry fails again, an administrator can check
                  the extraction service logs.
                </p>
              )}
            </div>
          )}
          {query.data.status === 'ready' && query.data.id && (
            <ReviewResults
              key={`${documentId}-${query.data.id}-${query.data.generation}`}
              extractionId={query.data.id}
              documentId={documentId}
              patientId={patientId}
              results={query.data.results}
              pages={query.data.pages}
              generation={query.data.generation}
              approved={query.data.approved_indices ?? []}
            />
          )}
        </>
      )}
    </section>
  );
}

function ReviewResults({
  extractionId,
  documentId,
  patientId,
  results,
  pages,
  generation,
  approved,
}: {
  documentId: string;
  patientId?: string | null;
  results: LabValue[];
  generation: number;
  extractionId: string;
  approved: number[];
  pages: { page: number; lines: string[]; confidence: number }[];
}) {
  const { can } = usePermissions();
  const patient = usePatient(can('read_patient') ? (patientId ?? undefined) : undefined);
  const pregnancies = usePregnancies(can('read_patient') ? (patientId ?? undefined) : undefined);
  const save = useLabAction(documentId, 'approve');
  const [rows, setRows] = useState(results);
  const [selected, setSelected] = useState<number[]>([]);
  const [pregnancyId, setPregnancyId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  function edit(index: number, field: keyof LabValue, value: string) {
    setRows((old) =>
      old.map((r, i) => (i === index ? { ...r, [field]: value || (field.endsWith('_date') ? null : '') } : r)),
    );
  }
  if (!results.length)
    return <p>No laboratory results were identified. Review the document if results were expected.</p>;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm">
        {results.length} candidate results. Compare selected values with the original document above. OCR can misread
        digits and flags.
      </p>
      {patient.data && (
        <p className="font-medium">
          Matched chart: {patient.data.metadata_?.first_name} {patient.data.metadata_?.last_name} · MRN{' '}
          {patient.data.medical_record_number} · DOB {patient.data.metadata_?.date_of_birth ?? 'not recorded'}
        </p>
      )}
      {!patientId && <p>Match or create the patient from Inbox before saving results to a pregnancy.</p>}
      {rows.map((row, i) => (
        <details key={i} className="rounded border p-3">
          <summary>
            {row.test_name}: {row.value} {row.unit} {row.flag && `(${row.flag})`} ·{' '}
            {row.collected_date ?? row.reported_date ?? 'Date missing'} · page {row.page}
            {approved.includes(i) && ' · Saved'}
          </summary>
          <FieldGroup className="mt-3">
            <Field orientation="horizontal">
              <Checkbox
                id={`lab-choose-${documentId}-${i}`}
                checked={selected.includes(i)}
                disabled={approved.includes(i) || save.isPending}
                onCheckedChange={(v) => setSelected((old) => (v ? [...old, i] : old.filter((n) => n !== i)))}
              />
              <FieldLabel htmlFor={`lab-choose-${documentId}-${i}`}>Include this result</FieldLabel>
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  'test_name',
                  'value',
                  'unit',
                  'collected_date',
                  'reported_date',
                  'reference_range',
                  'specimen',
                  'method',
                  'accession',
                  'loinc',
                ] as const
              ).map((field) => (
                <Field key={field}>
                  <FieldLabel htmlFor={`lab-${documentId}-${i}-${field}`}>
                    {
                      {
                        test_name: 'Test',
                        value: 'Result',
                        unit: 'Units',
                        collected_date: 'Collection date',
                        reported_date: 'Report date',
                        reference_range: 'Reference range',
                        specimen: 'Specimen',
                        method: 'Method',
                        accession: 'Accession',
                        loinc: 'LOINC (if verified)',
                      }[field]
                    }
                  </FieldLabel>
                  <Input
                    id={`lab-${documentId}-${i}-${field}`}
                    type={field.endsWith('_date') ? 'date' : 'text'}
                    value={row[field] ?? ''}
                    disabled={approved.includes(i) || save.isPending}
                    onChange={(e) => edit(i, field, e.target.value)}
                  />
                </Field>
              ))}
              <Field>
                <FieldLabel htmlFor={`flag-${documentId}-${i}`}>Laboratory flag</FieldLabel>
                <select
                  className="h-9 rounded-md border bg-background px-3"
                  id={`flag-${documentId}-${i}`}
                  disabled={approved.includes(i) || save.isPending}
                  value={row.flag}
                  onChange={(e) => edit(i, 'flag', e.target.value)}>
                  {['', 'N', 'H', 'L', 'HH', 'LL', 'A', 'AA', 'POS', 'NEG'].map((flag) => (
                    <option key={flag} value={flag}>
                      {flag || 'Not supplied'}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <p className="text-sm whitespace-pre-wrap">Source: {row.evidence}</p>
            <details>
              <summary>OCR text for page {row.page}</summary>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-xs">
                {pages.find((p) => p.page === row.page)?.lines.join('\n')}
              </pre>
            </details>
          </FieldGroup>
        </details>
      ))}
      {patientId && can('update_patient') && can('update_document') && (
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor={`lab-pregnancy-${documentId}`}>Destination pregnancy</FieldLabel>
            <select
              id={`lab-pregnancy-${documentId}`}
              className="h-9 rounded-md border bg-background px-3"
              value={pregnancyId}
              onChange={(e) => {
                setPregnancyId(e.target.value);
                setConfirmed(false);
              }}>
              <option value="">Choose pregnancy</option>
              {pregnancies.data?.data.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.status} · EDD {p.edd ?? 'unknown'} · {p.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </Field>
          <Field orientation="horizontal">
            <Checkbox
              id={`labs-confirm-${documentId}`}
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(v === true)}
            />
            <FieldLabel htmlFor={`labs-confirm-${documentId}`}>
              I verified these results belong to the matched patient and selected pregnancy, and checked the dates and
              values against the source.
            </FieldLabel>
          </Field>
          <Button
            disabled={!selected.length || !pregnancyId || !confirmed || save.isPending}
            onClick={() =>
              save.mutate(
                {
                  extraction_id: extractionId,
                  pregnancy_id: pregnancyId,
                  generation,
                  rows: selected
                    .filter((i) => !approved.includes(i))
                    .map((i) => ({ source_index: i, result: rows[i] })),
                },
                {
                  onSuccess: () => {
                    setSelected([]);
                    setConfirmed(false);
                    toast.success('Reviewed results saved to Maternal Flowsheet');
                  },
                  onError: () =>
                    toast.error(
                      'Unable to save. Check result dates and patient matching, then reload if the extraction changed.',
                    ),
                },
              )
            }>
            Save {selected.length || ''} reviewed results
          </Button>
        </FieldGroup>
      )}
    </div>
  );
}
